import { Link, useMatch, useLocation, PathMatch } from "react-router-dom";
import styled from "styled-components";
import {
  motion,
  useAnimation,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import RegimeLogo from "../../images/REGIME_logo.svg";

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 8vw;
  border-bottom: 1px solid var(--ink-06);
  backdrop-filter: blur(14px);

  @media (max-width: 480px) {
    padding: 12px 5vw;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  text-decoration: none;
`;

const LogoMark = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 48px;
    height: 48px;
  }

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;

    img {
      width: 38px;
      height: 38px;
    }
  }
`;

const Wordmark = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WordmarkPrimary = styled.span`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink);
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 16px;
    letter-spacing: 0.24em;
  }
`;

const WordmarkSub = styled.span`
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--accent-2);
  line-height: 1;
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Menu = styled.ul<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 1024px) {
    position: absolute;
    top: 72px;
    right: 20px;
    left: 20px;
    flex-direction: column;
    background: rgba(11, 11, 13, 0.97);
    border: 1px solid var(--ink-08);
    padding: 24px;
    gap: 16px;
    display: ${(props) => (props.$open ? "flex" : "none")};
    backdrop-filter: blur(16px);
    z-index: 10;
  }

  @media (max-width: 480px) {
    top: 64px;
    right: 5vw;
    left: 5vw;
  }
`;

const MenuItem = styled.li<{ $active?: boolean }>`
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(props) => (props.$active ? "var(--accent)" : "var(--ink)")};
  position: relative;
  padding-bottom: 4px;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: ${(props) => (props.$active ? "100%" : "0%")};
    height: 1px;
    background: var(--accent);
    transition: width 0.2s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const CTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(243, 239, 230, 0.14);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.03);
  color: var(--ink);
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(214, 179, 106, 0.5);
    background: rgba(214, 179, 106, 0.08);
  }

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: center;
  }
`;

const MenuToggle = styled.button`
  display: none;
  border: none;
  background: transparent;
  color: var(--ink);
  font-size: 22px;
  cursor: pointer;

  @media (max-width: 1024px) {
    display: inline-flex;
    align-items: center;
  }
`;

const navVariants = {
  top: { backgroundColor: "rgba(10, 10, 14, 0.4)" },
  scroll: { backgroundColor: "rgba(10, 10, 14, 0.92)" },
};

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const homeMatch: PathMatch<string> | null = useMatch("/");
  const aboutMatch: PathMatch<string> | null = useMatch("/about");
  const contactMatch: PathMatch<string> | null = useMatch("/contact");
  const musicMatch: PathMatch<string> | null = useMatch("/music");
  const navAnimation = useAnimation();
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 80) {
      navAnimation.start("scroll");
    } else {
      navAnimation.start("top");
    }
  });

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Close mobile menu on route change
  useEffect(() => { closeMenu(); }, [location.pathname, closeMenu]);

  // Close mobile menu on Escape key
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && menuOpen) closeMenu();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen, closeMenu]);

  return (
    <Nav variants={navVariants} animate={navAnimation} initial="top">
      <Brand to="/" aria-label="Regime Music Societe home">
        <LogoMark>
          <img src={RegimeLogo} alt="Regime Music Societe" width={48} height={48} />
        </LogoMark>
        <Wordmark>
          <WordmarkPrimary>Regime</WordmarkPrimary>
          <WordmarkSub>Music Societe</WordmarkSub>
        </Wordmark>
      </Brand>
      <NavGroup>
        <Menu $open={menuOpen} id="mobile-menu" role="navigation" aria-label="Main navigation">
          <MenuItem $active={homeMatch?.pattern.end}>
            <Link to="/">Home</Link>
          </MenuItem>
          <MenuItem $active={aboutMatch !== null}>
            <Link to="/about">About</Link>
          </MenuItem>
          <MenuItem $active={musicMatch !== null}>
            <Link to="/music">Music</Link>
          </MenuItem>
          <MenuItem $active={contactMatch !== null}>
            <Link to="/contact">Contact</Link>
          </MenuItem>
          <CTA to="/contact">Get in touch</CTA>
        </Menu>
        <MenuToggle
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {menuOpen ? "✕" : "☰"}
        </MenuToggle>
      </NavGroup>
    </Nav>
  );
}

export default Header;
